import { ClearApiRequestMeta } from '../../types/axios';
import { saveTokens } from '../auth';
import { CAPTCHA_KEY, getCaptchaToken } from '../captchaHelper';
import { clearApiClient } from './clearApiClient';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
}

export const REFRESH_TOKEN_ROUTE = '/web/v1/account/refresh';

/**
 * Sends refresh token request and saves tokens to Cookies
 * @param {string} refreshToken
 */
export const sendTokenRefreshRequest = (refreshToken: string) =>
  clearApiClient.post<TokenResponse>(REFRESH_TOKEN_ROUTE, { refresh_token: refreshToken }).then(
    (response) => {
      saveTokens(response.data.access_token, response.data.refresh_token);
    },
    (err) => {
      throw err;
    },
  );

/**
 * Exchanges `permanent_token` for a pair of regular ones (`access_token` and `refresh_token`) and saves them to Cookies
 * @param {string} permanentToken
 */
export const sendPermTokenRequest = (permanentToken: string, requestMeta: ClearApiRequestMeta) =>
  clearApiClient
    .post<TokenResponse>(
      '/web/v1/account/sign-in/perm-token',
      { perm_token: permanentToken },
      requestMeta && { meta: requestMeta },
    )
    .then((response) => {
      saveTokens(response.data.access_token, response.data.refresh_token);
    });

export const TEMP_TOKEN_SIGN_UP_ROUTE = '/web/v1/account/sign-up-temporarily-token-exchange';

/**
 * Exchanges `token` acquired from `/web/v1/account/sign-up-temporarily` flow for a pair of regular ones (`access_token` and `refresh_token`) and saves them to Cookies
 * @param {string} token
 */
export const sendTemporalSignUpExghangeRequest = (token: string) =>
  clearApiClient.post<TokenResponse>(TEMP_TOKEN_SIGN_UP_ROUTE, { token }).then(
    (response) => {
      if (response.data.access_token) {
        saveTokens(response.data.access_token, response.data.refresh_token);
      }
    },
    (error) => {
      throw error;
    },
  );

/**
 * Creates a permanent auth token for the current user
 * @returns permanent token, that can be exchanged via `/web/v1/account/sign-in/perm-token`
 */
export const sendCreatePermanentTokenRequest = (requestMeta?: ClearApiRequestMeta): Promise<string> =>
  clearApiClient
    .post<{ perm_token: string }>('/web/v1/account/session/perm-token', undefined, requestMeta && { meta: requestMeta })
    .then((response) => response.data.perm_token);

/**
 * Sends sign-in request with email and passwords.
 * Stores tokens in Cookies if successfull.
 */
export const sendSignInRequest = (email: string, password: string) =>
  getCaptchaToken()
    .then(
      (token) =>
        clearApiClient.post<TokenResponse>('/web/v1/account/sign-in', {
          email,
          password,
          user_id: '01FF4RSMQ5D6M4965RTCZ8Z5SA',
          recaptcha_token: token,
          recaptcha_site_key: CAPTCHA_KEY,
        }),
      (err) => {
        throw err;
      },
    )
    .then(
      (response) => {
        if (response.data.access_token) {
          saveTokens(response.data.access_token, response.data.refresh_token);
        }

        return response.data;
      },
      (err) => {
        throw err;
      },
    );

/**
 * Sends a verification email.
 */
export const sendVerificationEmailRequest = () => clearApiClient.post('/web/v1/account/resend-verification-email');

/**
 * Verifies user's email.
 */
export const sendVerifyEmailRequest = (hash: string) => clearApiClient.post('/web/v1/account/verify-email', { hash });

/**
 * Signs in a user via account code.
 */
export const sendSignInWithAccountCodeRequest = (code: string) =>
  clearApiClient.post<TokenResponse>('/web/v1/account/account-code/sign-in', { code }).then((response) => {
    saveTokens(response.data.access_token, response.data.refresh_token);
  });

/**
 * Creates a new user with email + password.
 * Saves response tokens to cookies.
 */
export const sendSignUpRequest = (email: string, password: string, isNewsletterSubAllowed: boolean) =>
  getCaptchaToken()
    .then((token) =>
      clearApiClient.post<TokenResponse>('/web/v1/account/sign-up', {
        email,
        password,
        receive_newsletters: isNewsletterSubAllowed,
        recaptcha_token: token,
        recaptcha_site_key: CAPTCHA_KEY,
      }),
    )
    .then((response) => {
      saveTokens(response.data.access_token, response.data.refresh_token);
    });

/**
 * Checks whether the supplied `referralId` is valid and "referred" to.
 */
export const sendCheckReferralRequest = (referralId: string) =>
  clearApiClient.post<{ exist: boolean }>('/web/v1/account/check-referral', { referral_id: referralId });

/**
 * Attaches this account to the referrer one.
 */
export const sendBindReferralRequest = (referralId: string, requestMeta: ClearApiRequestMeta) =>
  clearApiClient.post(
    '/web/v1/account/bind-referral',
    { referral_id: referralId },
    requestMeta && { meta: requestMeta },
  );

/**
 * Checks `currentPassword`, if it's correct, sets the account's password to `newPassword`.
 */
export const sendChangePasswordRequest = (currentPassword: string, newPassword: string) =>
  clearApiClient.post('/web/v1/account/new-password', {
    new_password: newPassword,
    old_password: currentPassword,
  });

/**
 * Saves `newPassword` for a user recorded in token.
 *
 * It's used for links from password reset emails.
 */
export const sendUpdatePasswordRequest = (newPassword: string, token: string) =>
  clearApiClient.post('/web/v1/account/change-password', {
    new_password: newPassword,
    hash: token,
  });

/**
 * Sends a password reset email.
 */
export const sendPasswordResetEmailRequest = (email: string) =>
  clearApiClient.post('/web/v1/account/reset-password', { email });

/**
 * Sets password for a user with no password.
 */
export const sendSetPasswordRequest = (password: string, requestMeta: ClearApiRequestMeta) =>
  clearApiClient.post('/web/v1/account/set-password', { new_password: password }, requestMeta && { meta: requestMeta });

/**
 * Gets a personalized deeplink to Diia Mobile App.
 */
export const sendDiiaDeepLinkRequest = () =>
  clearApiClient.get<{ deep_link: string }>('/web/v1/account/get-diia-deep-link').then((response) => response.data);

/**
 * Updates user's account setting(s).
 */
export const sendUpdateSettingsRequest = (isSubscribedToNewsletter: boolean) =>
  clearApiClient.post('/web/v1/account/update-settings', { receive_newsletters: isSubscribedToNewsletter });

/**
 * Removes device from the account.
 */
export const sendRemoveDeviceRequest = (deviceId: string) =>
  clearApiClient.delete('/web/v1/account/device', { data: { device_id: deviceId }, meta: {} });
